import { ORG_ADMIN, ORG_OWNER } from "@xapp/stentor-api-client/dist/models/Roles";
import { STENTOR_ADMIN } from "auth/modules/roles";
import SecondaryNavContainer, { SecondaryNavItem } from "components/SecondaryNav";
import { BETA_FLEET_MANAGEMENT, BETA_MAP_VIEW } from "constants/beta";
import { useGetOrgQuery } from "generated/graphql";
import React, { FC, useMemo } from "react";
import { createPortal } from "react-dom";
import { OrgBarTransitionNotice, useMovingMenuBar } from "./OrgBarTransitionNotice";

/** Subscription types for organizations */
export type SubscriptionTier = 'FREE' | 'PRO' | 'PARTNER' | 'ENTERPRISE';

export interface OrganizationSecondaryNavProps {
    readonly organizationId: string;
    /**
     * Optional subscription tier to avoid extra query if already available.
     * When provided, the component skips fetching organization data.
     */
    readonly subscription?: SubscriptionTier | string;
}

export const OrganizationSecondaryNav: FC<OrganizationSecondaryNavProps> = props => {
    const { organizationId, subscription } = props;

    // Always fetch org data (cache-first) - needed for beta flags even when subscription is provided
    const [getOrg] = useGetOrgQuery({
        variables: { orgId: organizationId },
        requestPolicy: 'cache-first',
    });

    const organization = getOrg.data?.org;
    // Organization uses 'type' field for subscription tier
    const isPartner = (subscription || organization?.type) === "PARTNER";
    const beta = organization?.beta;
    const hasFleetManagement = !!beta && typeof beta === "object" && !!beta[BETA_FLEET_MANAGEMENT];
    const hasMapView = !!beta && typeof beta === "object" && !!beta[BETA_MAP_VIEW];

    const navItems = useMemo<readonly SecondaryNavItem[]>(() => {
        const settingsSubmenu = [];

        // Add Branding submenu item only for PARTNER orgs
        if (isPartner) {
            settingsSubmenu.push({
                name: "Branding",
                path: `/${organizationId}/settings/branding`
            });
        }

        const dashboardSubmenu = [
            {
                name: "Map",
                path: `/${organizationId}/map`,
                ...(hasMapView ? {} : { showWithRoles: [STENTOR_ADMIN] }),
            },
        ];

        const items: SecondaryNavItem[] = [{
            name: "Dashboard",
            path: `/${organizationId}`,
            submenuItems: dashboardSubmenu
        },
        {
            name: "Leads",
            path: `/${organizationId}/leads`
        }];

        if (hasFleetManagement) {
            items.push({
                name: "Management",
                path: `/${organizationId}/management`
            });
        }

        items.push(
        {
            name: "Apps",
            path: `/${organizationId}/apps`
        },
        {
            name: "Analytics",
            path: `/${organizationId}/analytics`,
            showWithRoles: [STENTOR_ADMIN]
        },
        {
            name: "Form Conversion",
            path: `/${organizationId}/forms/dashboard`,
            showWithRoles: [STENTOR_ADMIN]
        },
        {
            name: "Members",
            path: `/${organizationId}/members`,
            showWithRoles: [ORG_ADMIN, ORG_OWNER]
        },
        {
            name: "Settings",
            path: `/${organizationId}/settings`,
            showWithRoles: [ORG_ADMIN, ORG_OWNER],
            submenuItems: settingsSubmenu.length > 0 ? settingsSubmenu : undefined
        });

        return items;
    }, [organizationId, isPartner, hasFleetManagement, hasMapView]);

    // Transition period: the bar stays, with a notice, until the user hides it or the date passes.
    const { show, hide: handleHide } = useMovingMenuBar();

    if (!show) {
        return null;
    }

    return createPortal(
        <SecondaryNavContainer
            items={navItems}
            navType="org"
            organizationId={organizationId}
            warningMessage={<OrgBarTransitionNotice onHide={handleHide} />}
        />,
        document.getElementById("secondary_nav_container")
    );
};
